import CalendarIcon from '@mui/icons-material/InsertInvitation';
import FormLabel from '@mui/material/FormLabel';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  NextIconButton,
  PreviousIconButton,
  SwitchViewButton,
} from '@openx/components-core/lib/CalendarNavigationButtons/CalendarNavigationButtons';
import { DEFAULT_DATE_FORMAT, DEFAULT_DATE_FORMAT_PLACEHOLDER } from '@openx/constants/date';
import { startOfDay } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFiltersContext } from '../../../../context/FiltersContext';
import { UtcTextFieldSlot } from './components/UtcTextFieldSlot';
import type { DateFieldErrorCode, DateRangeTuple, ValidateDatesResult } from './types';
import { buildErrorMessage, formatDate, parseDate, validateDates } from './utils';

export type DateRangeSelectorProps = {
  filterKey: string;
  inputFormat?: string;
  inputPlaceholder?: string;
  maxDate?: Date;
  minDate?: Date;
  onDateChange: (date: DateRangeTuple) => void;
  onInnerPopoverOpen: (open: boolean) => void;
  selectedOptions: DateRangeTuple;
  setValidationState?: (value: boolean) => void;
};

export const DateRangeSelector = ({
  filterKey,
  inputFormat = DEFAULT_DATE_FORMAT,
  inputPlaceholder = DEFAULT_DATE_FORMAT_PLACEHOLDER,
  maxDate = new Date(),
  minDate = new Date('2020-01-01'),
  onDateChange,
  onInnerPopoverOpen,
  selectedOptions,
  setValidationState,
}: DateRangeSelectorProps) => {
  const { t } = useTranslation();
  const [[startStr, endStr], setDates] = useState<DateRangeTuple>(selectedOptions);
  const [startErrorCode, setStartErrorCode] = useState<DateFieldErrorCode | undefined>(undefined);
  const [endErrorCode, setEndErrorCode] = useState<DateFieldErrorCode | undefined>(undefined);
  const { setFiltersErrors } = useFiltersContext();

  const startDate = parseDate(startStr, inputFormat);
  const endDate = parseDate(endStr, inputFormat);
  // Pickers are uncontrolled, so defaultValue must stay stable after mount
  const [[initialStartValue, initialEndValue]] = useState(() => [startDate, endDate]);

  const minBoundDate = useMemo(() => startOfDay(minDate), [minDate]);
  const maxBoundDate = useMemo(() => startOfDay(maxDate), [maxDate]);

  const applyValidationResult = useCallback(
    (result: ValidateDatesResult) => {
      const hasErrors = !!(result.start || result.end);
      const minBoundStr = formatDate(minBoundDate, inputFormat) ?? '';
      const maxBoundStr = formatDate(maxBoundDate, inputFormat) ?? '';

      const startMsg = result.start
        ? buildErrorMessage({ code: result.start, side: 'start', minDate: minBoundStr, maxDate: maxBoundStr })
        : undefined;
      const endMsg = result.end
        ? buildErrorMessage({ code: result.end, side: 'end', minDate: minBoundStr, maxDate: maxBoundStr })
        : undefined;

      setStartErrorCode(result.start);
      setEndErrorCode(result.end);
      setValidationState?.(hasErrors);
      setFiltersErrors(prev => ({
        ...prev,
        [filterKey]: hasErrors ? { start: startMsg, end: endMsg } : undefined,
      }));
    },
    [filterKey, inputFormat, maxBoundDate, minBoundDate, setFiltersErrors, setValidationState]
  );

  const handleStartChange = useCallback(
    (date: Date | null) => {
      const updatedStart = formatDate(date, inputFormat);
      const updated: DateRangeTuple = [updatedStart, endStr];
      const validationResult = validateDates({
        currentDate: date,
        otherDate: { date: endStr, error: endErrorCode },
        side: 'start',
        minDate: minBoundDate,
        maxDate: maxBoundDate,
        inputFormat,
      });

      applyValidationResult(validationResult);
      setDates(updated);

      if (validationResult.start === undefined) {
        onDateChange(updated);
      }
    },
    [applyValidationResult, endErrorCode, endStr, inputFormat, maxBoundDate, minBoundDate, onDateChange]
  );

  const handleEndChange = useCallback(
    (date: Date | null) => {
      const updatedEnd = formatDate(date, inputFormat);
      const updated: DateRangeTuple = [startStr, updatedEnd];
      const validationResult = validateDates({
        currentDate: date,
        otherDate: { date: startStr, error: startErrorCode },
        side: 'end',
        minDate: minBoundDate,
        maxDate: maxBoundDate,
        inputFormat,
      });

      applyValidationResult(validationResult);
      setDates(updated);

      if (validationResult.end === undefined) {
        onDateChange(updated);
      }
    },
    [applyValidationResult, inputFormat, maxBoundDate, minBoundDate, onDateChange, startErrorCode, startStr]
  );

  const minBoundStr = formatDate(minBoundDate, inputFormat) ?? '';
  const maxBoundStr = formatDate(maxBoundDate, inputFormat) ?? '';
  const startError = startErrorCode
    ? buildErrorMessage({ code: startErrorCode, side: 'start', minDate: minBoundStr, maxDate: maxBoundStr })
    : undefined;
  const endError = endErrorCode
    ? buildErrorMessage({ code: endErrorCode, side: 'end', minDate: minBoundStr, maxDate: maxBoundStr })
    : undefined;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'flex-start',
        }}
      >
        <Stack
          data-test="date-range-filter-start-date"
          spacing={0.5}
          sx={{
            flex: 1,
          }}
        >
          <FormLabel>{t('Start Date')}</FormLabel>
          <DatePicker
            onChange={handleStartChange}
            minDate={minBoundDate}
            maxDate={endDate ?? maxBoundDate}
            defaultValue={initialStartValue}
            format={inputFormat}
            onOpen={() => onInnerPopoverOpen(true)}
            onClose={() => onInnerPopoverOpen(false)}
            slots={{
              textField: UtcTextFieldSlot,
              openPickerIcon: CalendarIcon,
              previousIconButton: PreviousIconButton,
              nextIconButton: NextIconButton,
              switchViewButton: SwitchViewButton,
            }}
            slotProps={{
              textField: {
                error: !!startError,
                helperText: startError,
                slotProps: { htmlInput: { placeholder: inputPlaceholder } },
              },
              openPickerIcon: {
                // @ts-expect-error there is no type definition for data-* attributes on this slot, although it works
                'data-test': 'calendar-icon',
              },
            }}
          />
        </Stack>

        <Typography variant="body1" sx={{ marginTop: '20px !important' }}>
          &ndash;
        </Typography>

        <Stack
          data-test="date-range-filter-end-date"
          spacing={0.5}
          sx={{
            flex: 1,
          }}
        >
          <FormLabel>{t('End Date')}</FormLabel>
          <DatePicker
            defaultValue={initialEndValue}
            format={inputFormat}
            onChange={handleEndChange}
            minDate={startDate ?? minBoundDate}
            maxDate={maxBoundDate}
            onOpen={() => onInnerPopoverOpen(true)}
            onClose={() => onInnerPopoverOpen(false)}
            slots={{
              textField: UtcTextFieldSlot,
              openPickerIcon: CalendarIcon,
              previousIconButton: PreviousIconButton,
              nextIconButton: NextIconButton,
              switchViewButton: SwitchViewButton,
            }}
            slotProps={{
              textField: {
                error: !!endError,
                helperText: endError,
                slotProps: { htmlInput: { placeholder: inputPlaceholder } },
              },
              openPickerIcon: {
                // @ts-expect-error there is no type definition for data-* attributes on this slot, although it works
                'data-test': 'calendar-icon',
              },
            }}
          />
        </Stack>
      </Stack>
    </LocalizationProvider>
  );
};
